import './film.css';
import { Film, CHAPTERS } from './film';
import { StudioAudio } from '../studio/StudioAudio';

// Old studio links (/?studio) live on the game page now.
if (new URLSearchParams(location.search).has('studio')) location.replace('/play.html?studio');

const LAUNCH = new Date(2027, 0, 1).getTime();
const loadN = document.querySelector<HTMLElement>('#loadN')!;
const film = new Film((f) => (loadN.textContent = String(Math.round(f * 100))));
if (import.meta.env.DEV) (window as unknown as { __film: Film }).__film = film;
document.body.prepend(film.canvas);

// ---------- sound: off until you ask for it
const audio = new StudioAudio();
audio.muted = true;
const soundBtn = document.querySelector<HTMLButtonElement>('#sound')!;
soundBtn.addEventListener('click', async () => {
  const on = soundBtn.getAttribute('aria-pressed') !== 'true';
  if (on) { audio.setMuted(false); await audio.resume(); } else audio.setMuted(true);
  soundBtn.setAttribute('aria-pressed', String(on));
  soundBtn.querySelector('.sound-l')!.textContent = on ? 'Sound on' : 'Sound off';
});
film.onCue = (cue) => {
  if (cue === 'seal') audio.hit('crack');
  else if (cue === 'ignite') { audio.hit('ignite'); audio.hit('confirm'); }
  else if (cue === 'burn') audio.hit('portal', 0.5);
  else if (cue === 'roll') audio.hit('crack', 0.35);
  else if (cue === 'land') audio.hit('confirm', 0.5);
};

// ---------- the cursor finds things on the table
const tip = document.querySelector<HTMLElement>('#tip')!;
const toast = document.querySelector<HTMLElement>('#toast')!;
let lastX = 0, toastT = 0;
addEventListener('pointermove', (e) => {
  // Over the page's own UI (titles, form, nav), the table isn't what you're pointing at.
  const overUi = (e.target as HTMLElement).closest?.('.title.live, .nav, .chapters');
  film.pointer(overUi ? -9999 : e.clientX, overUi ? -9999 : e.clientY, e.clientX - lastX);
  lastX = e.clientX;
  tip.style.transform = `translate(${e.clientX + 16}px, ${e.clientY + 14}px)`;
});
film.onHover = (label) => {
  tip.textContent = label ?? '';
  tip.classList.toggle('on', !!label);
  document.body.classList.toggle('pointing', !!label);
};
film.canvas.addEventListener('pointerdown', (e) => { if (film.pointerDown()) e.preventDefault(); });
addEventListener('pointerup', () => film.pointerUp());
film.onRoll = (n) => {
  toast.textContent = n === 20 ? 'Natural 20. The studio approves.' : n === 1 ? 'You rolled a 1. The dragon noticed.' : `You rolled a ${n}.`;
  toast.classList.add('on');
  clearTimeout(toastT);
  toastT = window.setTimeout(() => toast.classList.remove('on'), 2600);
};

// ---------- titles rise in word by word
document.querySelectorAll<HTMLElement>('.title h1, .title h2').forEach((h) => {
  const words = (h.textContent ?? '').trim().split(/\s+/);
  h.setAttribute('aria-label', h.textContent ?? '');
  h.innerHTML = words.map((w, i) => `<span class="w" aria-hidden="true"><span style="--i:${i}">${w}</span></span>`).join(' ');
});

// ---------- scroll is the timeline (native scroll, so touch and keyboard just work)
const maxScroll = () => document.documentElement.scrollHeight - innerHeight;
let target = 0, idle = 0;
addEventListener('scroll', () => { target = scrollY / Math.max(1, maxScroll()); idle = 0; }, { passive: true });
const goTo = (i: number) => scrollTo({ top: CHAPTERS[i] * maxScroll(), behavior: 'smooth' });
document.querySelectorAll<HTMLElement>('[data-chapter]').forEach((el) => {
  if (el.tagName === 'SECTION') return;
  el.addEventListener('click', (e) => { e.preventDefault(); goTo(Number(el.dataset.chapter)); });
});

// ---------- titles: each chapter's text shows only while the camera holds on it
const titles = [...document.querySelectorAll<HTMLElement>('.title')];
const dots = [...document.querySelectorAll<HTMLElement>('.chapters button')];
const scrim = document.querySelector<HTMLElement>('#scrim')!;
const STEP = CHAPTERS[1] - CHAPTERS[0];
let shown = -1;
function updateTitles(p: number): void {
  // Exactly one chapter owns the frame: the nearest one, and only once the camera has
  // nearly arrived. Its words then rise on their own clock (CSS), so they never stall.
  let current = 0;
  CHAPTERS.forEach((c, i) => { if (Math.abs(p - c) < Math.abs(p - CHAPTERS[current])) current = i; });
  // Only once the camera has arrived and is headed nowhere else, so flying past a chapter
  // on a long scroll never flashes its title.
  const settled = Math.abs(p - CHAPTERS[current]) < STEP * 0.32 && Math.abs(target - CHAPTERS[current]) < STEP * 0.32;
  const next = settled ? current : -1;
  if (next !== shown) {
    titles.forEach((el, i) => el.classList.toggle('on', i === next));
    shown = next;
    if (next >= 0) scrim.dataset.side = titles[next].classList.contains('t-right') ? 'right' : 'left';
  }
  scrim.classList.toggle('on', next >= 0);
  titles.forEach((el, i) => el.classList.toggle('live', i === next));
  // The rail names sections; light the last one at or before where you are.
  const at = (d: HTMLElement) => Number(d.dataset.chapter);
  const lit = dots.filter((d) => at(d) <= current).pop();
  dots.forEach((d) => d.classList.toggle('now', d === lit));
  document.querySelector('#hint')!.classList.toggle('on', p < STEP * 0.3);
}

// ---------- countdown, on the scroll in 3D and in the title
const pad = (n: number) => String(n).padStart(2, '0');
function tick(): void {
  let s = Math.max(0, Math.floor((LAUNCH - Date.now()) / 1000));
  const d = Math.floor(s / 86400); s %= 86400;
  const h = Math.floor(s / 3600); s %= 3600;
  const m = Math.floor(s / 60); s %= 60;
  const text = `${d}d ${pad(h)}h ${pad(m)}m ${pad(s)}s`;
  film.setCountdown(text);
  document.querySelectorAll('[data-count]').forEach((el) => (el.textContent = `Project 01 lights up in ${text}`));
}

// ---------- early access: POST /api/join, then the candle catches
const form = document.querySelector<HTMLFormElement>('#joinForm')!;
const status = document.querySelector<HTMLElement>('#joinStatus')!;
const joined = (fresh: boolean) => {
  form.classList.add('done');
  document.querySelector('#joinH')!.textContent = fresh ? 'The candle’s lit. You’re on the list.' : 'Your candle’s still burning.';
  status.textContent = 'You’ll see Project 01 48 hours before anyone else.';
  if (fresh) film.ignite(); else film.setJoined();
};
try { if (localStorage.getItem('burnwood:ember')) joined(false); } catch { /* storage blocked */ }
form.addEventListener('submit', async (e) => {
  e.preventDefault();
  const email = (form.elements.namedItem('email') as HTMLInputElement).value.trim();
  const role = (form.elements.namedItem('role') as HTMLSelectElement).value;
  const website = (form.elements.namedItem('website') as HTMLInputElement).value;
  status.classList.remove('err');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
    form.classList.add('bad'); status.classList.add('err');
    status.textContent = 'That email doesn’t look right. Check it and try again.';
    return;
  }
  form.classList.remove('bad');
  const btn = form.querySelector<HTMLButtonElement>('button[type=submit]')!;
  btn.disabled = true;
  status.textContent = 'Striking a match…';
  try {
    const res = await fetch('/api/join', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, website, source: `site:${role}` }) });
    if (!res.ok) throw new Error(String(res.status));
    try { localStorage.setItem('burnwood:ember', '1'); } catch { /* fine */ }
    joined(true);
  } catch {
    status.classList.add('err');
    status.textContent = 'That didn’t go through. Try again in a moment.';
    btn.disabled = false;
  }
});

// ---------- run
(async () => {
  await document.fonts.ready; // the map's labels are drawn in IM Fell English
  await film.build();
  tick();
  setInterval(tick, 1000);
  document.querySelector('#loader')!.classList.add('done');
  document.documentElement.classList.add('ready');
  let prev = performance.now();
  const frame = (now: number) => {
    requestAnimationFrame(frame);
    if (document.hidden) return;
    const dt = Math.min(0.05, (now - prev) / 1000);
    prev = now;
    // Damped timeline, and a gentle snap to the nearest chapter once you stop.
    film.p += (target - film.p) * (1 - Math.exp(-dt * 3.2));
    idle += dt;
    const typing = document.activeElement?.matches('input, select');
    if (idle > 0.9 && idle < 0.9 + dt * 1.5 && !typing) {
      const near = CHAPTERS.reduce((a, c) => (Math.abs(c - target) < Math.abs(a - target) ? c : a));
      if (Math.abs(near - target) > 0.004 && Math.abs(near - target) < 0.09) scrollTo({ top: near * maxScroll(), behavior: 'smooth' });
    }
    updateTitles(film.p);
    audio.mix(0.5 + film.p * 0.3, 0.3 + film.p * 0.7, 0);
    film.update(dt);
  };
  requestAnimationFrame(frame);
})();
